import React from 'react';
import { EstateProfile } from '../../types';
import { Fieldset, TextInput, RadioGroup } from '../ui';

interface Props {
    data: EstateProfile;
    onChange: (data: EstateProfile) => void;
}

// Map between the existing schema's mixed booleans and the radio-group's string space.
const ynuFromBool = (b: boolean | null | undefined, uncertain?: boolean | null): string =>
    uncertain === true ? 'unsure' : b === true ? 'yes' : b === false ? 'no' : '';
const boolFromYnu = (v: string): boolean | null =>
    v === 'yes' ? true : v === 'no' ? false : null;

export const BasicsStep: React.FC<Props> = ({ data, onChange }) => {
    const deceased = data.deceased || {};
    const family = data.family || {};
    const ihtDetails = data.iht_details || {};
    const predeceased = ihtDetails.predeceased_spouse || {};

    const updateDeceased = (field: string, value: any) => {
        const updated: EstateProfile = {
            ...data,
            deceased: { ...deceased, [field]: value },
        };
        // When marital status changes, sync legacy field and clear conditional spouse answers.
        if (field === 'maritalStatus') {
            const isMarried = value === 'MARRIED_OR_CP';
            updated.family = {
                ...updated.family,
                surviving_spouse_or_civil_partner: isMarried ? 'yes' : 'no',
                current_spouse_alive: isMarried ? updated.family?.current_spouse_alive ?? null : 'na',
                assets_passed_to_spouse: isMarried ? updated.family?.assets_passed_to_spouse : undefined,
            };
        }
        onChange(updated);
    };

    const updateFamily = (field: string, value: any) => {
        const nextFamily = { ...family, [field]: value };
        // If the spouse is no longer alive (or marital changes), the "expect to pass" question is moot.
        if (field === 'current_spouse_alive' && value !== 'yes') {
            nextFamily.assets_passed_to_spouse = undefined;
        }
        onChange({ ...data, family: nextFamily });
    };

    const updatePredeceased = (
        next: { exists?: boolean | null; exists_uncertain?: boolean | null }
    ) => {
        onChange({
            ...data,
            iht_details: {
                ...ihtDetails,
                predeceased_spouse: { ...predeceased, ...next },
            },
        });
    };

    const updateMainResidenceToDescendants = (v: string) => {
        onChange({
            ...data,
            iht_details: {
                ...ihtDetails,
                main_residence_to_descendants: (v || null) as 'yes' | 'no' | 'unsure' | null,
            },
        });
    };

    const todayIso = new Date().toISOString().split('T')[0];

    const isMarriedOrCp = deceased.maritalStatus === 'MARRIED_OR_CP';
    const showSpousePassQuestion = isMarriedOrCp && family.current_spouse_alive === 'yes';
    // §1B applies even to widowed/divorced — the previous spouse could still
    // have predeceased. We only hide it for "Never married" where the question
    // is meaningless.
    const showPredeceasedQuestion =
        !!deceased.maritalStatus && deceased.maritalStatus !== 'NEVER_MARRIED';

    return (
        <div className="max-w-[640px] mx-auto">
            <p className="font-serif italic text-[15px] text-ink2 mb-10 leading-relaxed">
                These details set the legal frame for the rest of the review — domicile,
                marital status, and date of death determine which allowances apply.
            </p>

            <Fieldset
                label="Date of death"
                error={
                    !deceased.date_of_death
                        ? 'Please enter the date of death to continue.'
                        : undefined
                }
            >
                <TextInput
                    type="date"
                    max={todayIso}
                    value={deceased.date_of_death || ''}
                    onChange={(e) => updateDeceased('date_of_death', e.target.value)}
                />
            </Fieldset>

            <Fieldset
                label="Domicile at date of death"
                help="Domicile determines whether UK Inheritance Tax applies to worldwide assets. If unsure, choose ‘Unsure’ — we'll flag what to check."
            >
                <RadioGroup
                    name="domicile"
                    value={deceased.domicile || ''}
                    onChange={(v) => updateDeceased('domicile', v)}
                    options={[
                        {
                            value: 'UK',
                            label: 'UK domiciled',
                            description: 'Standard IHT rules apply to the worldwide estate.',
                        },
                        {
                            value: 'NON_UK',
                            label: 'Non-UK domiciled',
                            description: 'IHT typically limited to UK-situs assets.',
                        },
                        {
                            value: 'UNSURE',
                            label: 'Unsure',
                            description: "We'll flag the residence and domicile factors to check.",
                        },
                    ]}
                />
            </Fieldset>

            <Fieldset
                label="Marital or civil-partnership status"
                help="Determines spouse exemption and whether transferable allowances may apply."
            >
                <RadioGroup
                    name="maritalStatus"
                    value={deceased.maritalStatus || ''}
                    onChange={(v) => updateDeceased('maritalStatus', v)}
                    options={[
                        { value: 'MARRIED_OR_CP', label: 'Married or in a civil partnership' },
                        { value: 'WIDOWED', label: 'Widowed' },
                        { value: 'DIVORCED_OR_SEPARATED', label: 'Divorced or legally separated' },
                        { value: 'NEVER_MARRIED', label: 'Never married, never in a civil partnership' },
                    ]}
                />
            </Fieldset>

            {/* §1A — surviving spouse status (only relevant when MARRIED_OR_CP) */}
            {isMarriedOrCp && (
                <Fieldset
                    label="Is the spouse or civil partner still alive?"
                    help="Affects spouse exemption and whether allowance transfer comes into play."
                >
                    <RadioGroup
                        name="current_spouse_alive"
                        value={family.current_spouse_alive && family.current_spouse_alive !== 'na'
                            ? family.current_spouse_alive
                            : ''}
                        onChange={(v) => updateFamily('current_spouse_alive', v)}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: 'unsure', label: 'Not sure' },
                        ]}
                    />
                </Fieldset>
            )}

            {/* §1D — reworded "expect assets to pass to a spouse" (gated on alive=yes) */}
            {showSpousePassQuestion && (
                <Fieldset
                    label="Do you expect assets to pass to a spouse or civil partner?"
                    help="This is only a general signal. You'll confirm where each asset passes later, which is what drives the tax calculation."
                >
                    <RadioGroup
                        name="assets_passed_to_spouse"
                        value={family.assets_passed_to_spouse || ''}
                        onChange={(v) => updateFamily('assets_passed_to_spouse', v)}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes (some or all)' },
                            { value: 'no', label: 'No' },
                            { value: 'unsure', label: 'Not sure' },
                        ]}
                    />
                </Fieldset>
            )}

            {/* §1B — previous spouse predeceased (transferable NRB / RNRB gate) */}
            {showPredeceasedQuestion && (
                <Fieldset
                    label="Did a previous spouse or civil partner die before them?"
                    help="If yes, unused inheritance-tax allowance from the first death may transfer. If not sure, we'll flag it for review and you can refine later."
                >
                    <RadioGroup
                        name="predeceased_spouse_exists"
                        value={ynuFromBool(predeceased.exists, predeceased.exists_uncertain)}
                        onChange={(v) => {
                            if (v === 'unsure') {
                                updatePredeceased({ exists: null, exists_uncertain: true });
                            } else {
                                updatePredeceased({ exists: boolFromYnu(v), exists_uncertain: false });
                            }
                        }}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: 'unsure', label: 'Not sure' },
                        ]}
                    />
                    {predeceased.exists_uncertain === true && (
                        <p className="mt-3 font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent">
                            We'll proceed and flag this for review. You can refine the answer in the
                            next steps if you find records of an earlier marriage.
                        </p>
                    )}
                </Fieldset>
            )}

            {/* §1C — main residence to descendants (high-level RNRB signal) */}
            <Fieldset
                label="Do you expect the deceased's share of the main home to pass to children or direct descendants?"
                help="Early signal for the residence nil-rate band. If the home was co-owned, answer only for the deceased's share — the other owner's share isn't part of the estate. Detailed allocation happens in the property step."
            >
                <RadioGroup
                    name="main_residence_to_descendants"
                    value={ihtDetails.main_residence_to_descendants || ''}
                    onChange={updateMainResidenceToDescendants}
                    inline
                    options={[
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                        { value: 'unsure', label: 'Not sure' },
                    ]}
                />
            </Fieldset>
        </div>
    );
};
